import { useLocation, useMatches, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useGetClassesApi, useGetOrganizationByIdApi } from 'eds-common/api/v3';
import { useGetMeApi } from 'eds-common/api/v2';
import { useUnitCollectionData } from 'eds-product-experience-ux';
import {
  populateDataLayerBase,
  populateDataLayerPage,
  populateDataLayerUser,
} from '@/services/adobe-data-layer/adobe-data-layer-service';
import { useClientEnvs } from '@/hooks/api/use-client-envs/use-client-envs';
import { ENV } from '@/env-variables';
import { useApplications } from '@/hooks/api/use-application/use-application';
import {
  APPLICATION_CODE_ROUTES,
  PAGE_TYPE,
  ROUTE_PRODUCT_MAPPING,
} from '@/constants';
import type { Application } from '@/services/composite-service/composite-service';

function usePopulateDataLayerUser() {
  const {
    data: user,
    isError: getMeError,
    isLoading: getMeLoading,
  } = useGetMeApi();

  const {
    data: classes,
    isError: getClassesError,
    isLoading: getClassesLoading,
  } = useGetClassesApi(
    {
      userId: user?.userId ?? '',
      orgId: user?.currentOrg?.orgId ?? '',
      source: user?.userSource ?? 'MyScholastic',
    },
    { enabled: user?.currentRole !== 'student' }
  );

  const {
    data: orgData,
    isError: getOrganizationError,
    isLoading: getOrgLoading,
  } = useGetOrganizationByIdApi({
    orgId: user?.currentOrg?.orgId,
  });

  useEffect(() => {
    if (getMeLoading || getClassesLoading || getOrgLoading) {
      return;
    }
    const hasClass =
      (user?.currentRole !== 'student' && classes && classes?.length > 0) ??
      false;
    if (user) {
      const parentOrgId = orgData?.parent_organization_id ?? null;

      populateDataLayerUser({
        currentRole: user.currentRole,
        grade: user.grade,
        hasClass,
        orgId: user.currentOrg.orgId,
        parentOrgId,
        userId: user.userId,
      });

      if (getMeError || getClassesError || getOrganizationError) {
        window.dumbleData.page.isError = true;
      }
    }
  }, [
    user,
    classes,
    getMeLoading,
    getClassesLoading,
    getOrgLoading,
    orgData?.parent_organization_id,
    getMeError,
    getClassesError,
    getOrganizationError,
  ]);
}

function getProductCodes(applications?: Application[], pathname?: string) {
  if (!applications || !pathname) {
    return null;
  }

  const productCodes = Object.keys(APPLICATION_CODE_ROUTES)
    .filter((applicationCode) =>
      pathname.startsWith(APPLICATION_CODE_ROUTES[applicationCode] ?? '')
    )
    .flatMap((applicationCode) => {
      const productCode =
        ROUTE_PRODUCT_MAPPING[APPLICATION_CODE_ROUTES[applicationCode] ?? ''];

      return (
        applications.find((app) => app.applicationCode === productCode)
          ?.productCodes ?? []
      );
    });

  return productCodes.length > 0 ? productCodes : null;
}

function usePopulateDataLayerPage() {
  const { unitId } = useParams();
  const {
    data: collectionData,
    isError: collectionError,
    isLoading: collectionLoading,
  } = useUnitCollectionData(unitId);

  const location = useLocation();
  const matches = useMatches();
  const {
    data: applications,
    isError: applicationError,
    isLoading: applicationLoading,
  } = useApplications();

  useEffect(() => {
    if (collectionLoading || applicationLoading) {
      return;
    }
    const currentMatch = matches[matches.length - 1];
    const pathname = currentMatch?.pathname;

    const productCodes = getProductCodes(applications, pathname);

    const destinationUrl = window.location.href;

    const pageName = currentMatch?.handle?.title ?? null;
    const pageType = currentMatch?.handle?.pageType ?? null;
    const siteSection =
      matches.findLast((match) => match.handle?.siteSection)?.handle
        ?.siteSection ?? null;
    let bundleFocus = null;
    let bundleType = null;

    if (pageType === PAGE_TYPE.bundle) {
      bundleFocus = collectionData?.content?.focus ?? null;
      bundleType = collectionData?.content?.template ?? null;
    }
    if (applicationError || collectionError) {
      window.dumbleData.page.isError = true;
    }

    populateDataLayerPage({
      bundleFocus,
      bundleType,
      destinationUrl,
      productCodes,
      pageName,
      pageType,
      siteSection,
    });
  }, [
    location,
    matches,
    applications,
    collectionData,
    collectionError,
    applicationError,
    collectionLoading,
    applicationLoading,
  ]);
}

function useResetDataLayerError() {
  const location = useLocation();
  useEffect(() => {
    window.dumbleData.page.isError = false;
  }, [location]);
}

function usePopulateDataLayerBase() {
  const { data: clientEnvs } = useClientEnvs();
  const sysEnv = clientEnvs?.env ?? ENV;
  populateDataLayerBase({ sysEnv });
}

export function usePopulateDataLayer() {
  useResetDataLayerError();
  usePopulateDataLayerBase();
  usePopulateDataLayerUser();
  usePopulateDataLayerPage();
}
